// Classifies a couch-login failure as an EXPECTED, recoverable condition (a network blip,
// a Cloud Run scale-up race, or the server being behind this account's data version) versus
// a genuinely unexplained failure. Shared by the cold-login fallback, the retry-exhaustion
// report gate, and OnlineStateService's coming-online refresh in auth.service.ts /
// online-state.service.ts, so those call sites cannot drift apart on what counts as
// "recoverable". Extracted as a pure module (no Angular deps) so it is node-testable —
// same pattern as couch-login-online-gate.ts and couch-login-retry-gate.ts.

// Angular-free leaf module (see its own header comment) — importing from
// cloud-functions.service.ts directly would pull in @angular/core and this whole
// file's Angular dependency chain, which is NOT node-testable.
import { ACCOUNT_DELETED_HTTP_STATUS } from '@cf-shared/couch-auth';
import { CloudFunctionHttpError, isNetworkError } from './cloud-functions-errors';
import { isRetriableHttpError } from './cloud-functions-retry';

/**
 * HTTP status couchLogin returns when the `_users` doc backing this account is at a
 * format version newer than this server understands (#4283, `UsersDocTooNewError`
 * server-side — see server/functions/src/couchdb-helpers.ts and
 * declared-function-dispatch.ts). The condition resolves itself once the live server
 * is upgraded to understand that version, so it belongs in the same "expected,
 * recoverable" bucket as a network blip, not the "something is broken" bucket.
 */
// RESERVED: 409 identifies this one condition, and the client has nothing else
// to key on — the wrapper keeps only the status, not the response body. So a new
// `DeclaredFunctionHttpError` subclass must NOT reuse 409 without first giving
// the client a discriminator. A write conflict is the tempting second user of
// this status, and it would make every screen below tell the operator to wait a
// few minutes for something that never resolves itself, while suppressing the
// Sentry report that would have surfaced it.
export const SERVER_BEHIND_ACCOUNT_VERSION_HTTP_STATUS = 409;

/**
 * True when `err` is specifically the "this server is behind this account's data
 * version" refusal (see `SERVER_BEHIND_ACCOUNT_VERSION_HTTP_STATUS` above).
 */
export function isVersionMismatchCouchLoginError(err: unknown): boolean {
  return err instanceof CloudFunctionHttpError && err.httpStatus === SERVER_BEHIND_ACCOUNT_VERSION_HTTP_STATUS;
}

/**
 * True when `err` is specifically the "this account has been deleted" refusal
 * (#5136, `df-ab9038b7aa20`).
 *
 * The status itself is `ACCOUNT_DELETED_HTTP_STATUS` in `@cf-shared/couch-auth` —
 * ONE copy, which both this file and the server's `couchLogin` import. The 409
 * above is declared separately on each side and has to be kept in step by hand;
 * that is the shape being avoided here, not the one being followed.
 */
export function isAccountDeletedCouchLoginError(err: unknown): boolean {
  return err instanceof CloudFunctionHttpError && err.httpStatus === ACCOUNT_DELETED_HTTP_STATUS;
}

/**
 * True when `err` is the iOS system timeout (NSURLErrorTimedOut), which WKWebView surfaces as a
 * plain `Error`, or a plain `{ message }` object, reading "The request timed out." (df-97e69e66c032).
 *
 * Deliberately NOT part of `isRecoverableCouchLoginError`: the shared classifier keeps this
 * phrase non-transient so a slow server stays visible (`error-patterns.test.ts` pins it), and the
 * couch-login retry loop reports it as `Auth: Couch login - too many retries`. Only the
 * coming-online decision uses this, to stop calling that same timeout an authentication failure.
 */
export function isOsRequestTimeoutCouchLoginError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' && message.toLowerCase().includes('the request timed out');
}

/**
 * #6231: why a couch-login attempt ended WITHOUT calling the server. These exits store no
 * error, so without this a caller could not tell "never asked" from "asked and was refused".
 */
export enum CouchLoginSkipReason {
  // the connection state was Offline and a fresh reachability re-probe also failed
  OfflineAtStart = 'OfflineAtStart',
  // Firebase had not restored the signed-in user in time
  NoFirebaseUser = 'NoFirebaseUser',
  // Firebase has a user but could not hand out a sign-in token (CloudFunctionAuthError)
  AuthTokenUnavailable = 'AuthTokenUnavailable',
}

/**
 * True when a couch-login failure is an EXPECTED, recoverable condition — a network
 * failure, a Firebase network error, a retriable Cloud Run 502/503, the
 * server-behind-account-version refusal above, or the account having been deleted —
 * rather than the server genuinely rejecting us or something being broken.
 *
 * A deleted account is "expected" in the only sense this function means: nothing is
 * broken and nobody needs to be paged. It does NOT resolve itself the way the others
 * do. What ends it is Firebase: the deleted account's refresh token stops working, so
 * within about an hour the SDK signs this device out and it lands on the login screen
 * by itself. Until then the app carries on offline against its local data, exactly as
 * it does for a network blip. Counting it here is what stops each retry filing a
 * silent-problem report for a condition that is understood. `isNetworkError` covers the wrapped
 * CloudFunctionNetworkError, fetch TypeErrors, timeouts, aborts, and the iOS + Android
 * native connectivity-loss shapes ("Unable to resolve host …", "Software caused
 * connection abort" — the APP-3WY/APP-3Z9 mislabel class, #4010 hygiene).
 */
export function isRecoverableCouchLoginError(err: unknown): boolean {
  // note: AbortError shapes need no own clause — isNetworkError's delegation to
  // isTransientFetchError already matches both the DOMException and the plain-object
  // AbortError forms
  return isNetworkError(err)
    // Firebase's own network-error code. Checked structurally (a `code` field, not
    // `instanceof FirebaseError`) so this module never imports 'firebase/app' — that
    // package is not node-loadable in this project's plain mocha/ts-node test harness
    // (see auth.service.ts's own "can't be loaded under node" note), and importing it
    // here would defeat the whole point of extracting a pure, node-testable classifier.
    || (err instanceof Error && (err as Error & { code?: string }).code === 'auth/network-request-failed')
    || isRetriableHttpError(err)
    || isVersionMismatchCouchLoginError(err)
    || isAccountDeletedCouchLoginError(err);
}
